import { Button, Spinner } from '@shopify/polaris'
import React, { useEffect, useState, useMemo, useRef } from 'react'
import './style.css'
import { useSelector } from 'react-redux'
import useAppDispatch from '../../hooks/useAppDispatch'
import { fetchCompanyApp } from '../../redux/features/profile/profileSlice'
import { switchApp } from '../../redux/features/user/userSlice'
import { getAppIdFromStorage, isLocalhost } from '../../utils/appIdStorage'
import { getProjectsListForNavigation, getProjectsForSwitcher } from '../../projectConfig/projects'
import { buildProjectSwitchUrl } from '../../utils/workspaceSwitch/projectSwitchLanding'

const ProjectNavigationWindow = (props) => {
	const dispatch = useAppDispatch()
	const { companyApp, fetchingCompanyApp } = useSelector((state) => state.profile)
	const { openProjectName } = useSelector(state => state.user)
	// Get app_id from localStorage instead of Redux
	const app_id = getAppIdFromStorage()
	const [selectedApp, setSelectedApp] = useState(null)
	const hasFetchedCompanyAppRef = useRef(false)

	// Get projects from centralized config
	const allProjectsList = useMemo(() => getProjectsListForNavigation(), [])
	const projectsForSwitcher = useMemo(() => getProjectsForSwitcher(), [])

	// Fetch company apps only once per modal mount to avoid infinite request loop
	useEffect(() => {
		if (hasFetchedCompanyAppRef.current) return
		hasFetchedCompanyAppRef.current = true
		dispatch(fetchCompanyApp())
	}, [dispatch])

	// Normalize /company/app response: can be array ["1", "2"] or object { 0: "1", 1: "2" }
	const selectedIds = useMemo(() => {
		if (!companyApp) return []
		const list = Array.isArray(companyApp) ? companyApp : Object.values(companyApp)
		return list.map(id => Number(id)).filter(n => !Number.isNaN(n))
	}, [companyApp])

	// Show only apps the company has access to (exactly what server sent in /company/app — no extra items)
	const isLoadingApps = fetchingCompanyApp === true
	const displayArr = useMemo(() => {
		if (isLoadingApps) return []
		return allProjectsList.filter(item => selectedIds.includes(item.id))
	}, [allProjectsList, selectedIds, isLoadingApps])

	// If app_id !== 0, automatically select project
	useEffect(() => {
		if (app_id !== 0 && openProjectName) {
			setSelectedApp(openProjectName)
		}
	}, [openProjectName, app_id])

	// By default, highlight the first element if nothing is selected
	useEffect(() => {
		if (displayArr.length > 0 && !selectedApp) {
			setSelectedApp(displayArr[0].value)
		}
	}, [displayArr, selectedApp])

	const changeProjectHandler = async () => {
		// Find selected project in switcher projects (has href and appId)
		const selectedProject = projectsForSwitcher.find(project =>
			selectedApp && selectedApp.toLowerCase() === project.name.toLowerCase()
		)
		
		if (!selectedProject) {
			alert('Please select a valid Lappa App to continue.')
			return
		}
		
		try {
			// Wait for switchApp to complete before redirecting (useAppDispatch already unwraps, so no .unwrap() here)
			const result = await dispatch(switchApp(selectedProject.appId))
			if (result?.error) {
				alert(result.error === 'Unauthorized' ? 'Session expired or unauthorized. Please log in again.' : result.error)
				return
			}
			// Save app_id to localStorage (only if not localhost)
			if (!isLocalhost()) {
				localStorage.setItem('app_id', selectedProject.appId.toString())
			}
			// Redirect after successful switch (settings hub: not every project has the current page)
			window.location.href = buildProjectSwitchUrl(selectedProject.href)
		} catch (e) {
			console.error('Error switching app:', e)
			const message = e?.payload || e?.message || 'Failed to switch app. Please try again.'
			alert(message)
		}
	}

	return (
			<div className='topbar-wrapper_block-modal'>
				<div className='topbar-wrapper_block-modal_wrapper'>
					<h3 className='topbar-wrapper_block-modal_wrapper-title'>
						Select a Lappa App to Begin
					</h3 >
					<p className='topbar-wrapper_block-modal_wrapper-subtitle'>
						Get started on Lappa apps. No worries, you can always add another app.
					</p >
					{isLoadingApps ? (
						<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0', gap: 16 }}>
							<Spinner accessibilityLabel="Loading apps" size="large" />
							<span style={{ color: '#707070', fontSize: 14 }}>Loading your apps...</span>
						</div>
					) : (
						<>
							<ul className='topbar-wrapper_block-modal_wrapper-list'>
								{
									displayArr?.map((item, index) => (
										<li
											className={`topbar-wrapper_block-modal_wrapper-list_item ${selectedApp === item.value ? 'selected-line' : ''}`}
											key={item.id ?? index}
											onClick={() => setSelectedApp(item.value)}
										>
											<img
												src={item?.img}
												alt={item?.label}
												className='topbar-wrapper_block-modal_wrapper-list_item-icon'
											/>
											<div style={{ marginLeft: '10px', paddingRight: 10 }}>
												<h3 className='topbar-wrapper_block-modal_wrapper-list_item-title'>{item.label}</h3>
												{selectedApp === item.value && item.description ? (
													<p className='topbar-wrapper_block-modal_wrapper-list_item-descriptoin'>
														{item.description}
													</p>
												) : null}
											</div>
										</li>
									))
								}
							</ul>
							<Button onClick={changeProjectHandler} variant={'primary'} fullWidth disabled={!displayArr?.length}>
								Continue
							</Button>
						</>
					)}
				</div>
			</div>
	)
}

export default ProjectNavigationWindow
